import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const apiVersion = "2026-01-01";

const apiHost = (region?: string) => {
  switch (region) {
    case "eu":
      return "api.eu.wdesk.com";
    case "apac":
      return "api.apac.wdesk.com";
    case "ca":
      return "api.ca.wdesk.com";
    default:
      return "api.app.wdesk.com";
  }
};

export const workiva = defineConnector({
  identifier: "workiva",
  name: "Workiva",
  tagLine: "Reporting and compliance workflows",
  logo,
  tags: ["productivity", "data"],
  protocol: "http",
  defaultUrlPattern: ({ region }) =>
    `^https://${apiHost(region).replaceAll(".", "\\.")}/(?:documents|spreadsheets|tasks)(?:[/?].*)?$`,
  auth: (app) =>
    fetchTokenAuth({
      request: {
        url: `https://${apiHost(app.configuration.region)}/oauth2/token`,
        method: "POST",
        headers: [
          {
            name: "Content-Type",
            value: "application/x-www-form-urlencoded;charset=UTF-8",
          },
          { name: "Accept", value: "application/json" },
          { name: "X-Version", value: apiVersion },
        ],
        formBody: {
          grant_type: "client_credentials",
          client_id: app.config.clientId,
          client_secret: app.secret.clientSecret,
        },
      },
      extract: { from: "jsonPath", paths: ["access_token"] },
      apply: {
        kind: "header",
        scheme: "Bearer",
        extraHeaders: [{ name: "X-Version", value: apiVersion }],
      },
    }),
  steps: [
    {
      title: "Prepare Workiva API access",
      items: [
        {
          kind: "markdown",
          content:
            "Ask an Org Security Administrator, Workspace Owner, or Org Workspace Admin to create an API grant for a dedicated integration user, following the [Workiva setup guide](https://developers.workiva.com/2026-01-01/setup.html). Give the user access only to the files and tasks your workflows need. Include **file:read** for the read-only connection test, and add only the scopes required by your workflows, such as **task:read** for listing tasks. Editing requires the relevant write scopes and user permissions. Set the grant expiration and any IP restrictions according to your organization’s policies.",
        },
        {
          kind: "markdown",
          content:
            "The integration user can reveal the grant’s secret in their user profile under **Security**, using **Actions > Regenerate**. The secret is shown only once; regenerating it replaces the existing secret. Copy the client ID and secret for this connector. Update the connector if the grant expires or the secret changes.",
        },
      ],
    },
    {
      title: "Enter your Workiva details",
      items: [
        {
          kind: "select",
          label: "Region",
          target: { kind: "configuration", field: "region" },
          options: [
            { label: "US", value: "us" },
            { label: "EU", value: "eu" },
            { label: "APAC", value: "apac" },
            { label: "Canada", value: "ca" },
          ],
          defaultValue: "us",
          required: true,
          helperText: "Select the location of your integration user.",
        },
        {
          kind: "input",
          label: "Client ID",
          target: { kind: "configuration", field: "clientId" },
          required: true,
        },
        {
          kind: "input",
          label: "Client secret",
          target: { kind: "secrets", field: "clientSecret" },
          required: true,
        },
        {
          kind: "markdown",
          content:
            "This connector uses Workiva’s **2026-01-01 Platform API** for documents, spreadsheets, and tasks. It exchanges your client credentials automatically and adds the bearer token and API version header. The connection test lists documents without changing content; an empty list is a successful response. Chains, Wdata, and legacy API paths are not included.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ region }) => ({
      url: `https://${apiHost(region)}/documents`,
      method: "GET",
      headers: { Accept: "application/json", "X-Version": apiVersion },
      expectedStatus: 200,
      expectedBodyContains: '"data"',
    }),
  },
  publicEnvVars: ({ region }) => ({
    WORKIVA_URL: `https://${apiHost(region)}`,
  }),
  usageHint: ({ region }) =>
    `Use Workiva’s ${apiVersion} Platform API at https://${apiHost(region)} with /documents, /spreadsheets, or /tasks. The connector supplies Authorization and X-Version headers automatically. Access is limited by the API grant’s scopes and integration user’s permissions.`,
});
